import { lmsGet, lmsPost, lmsDelete } from "./lmsApiClient";
import { GLOBAL_API } from "../actionTypes/types";
import { setPasswordBaseUrl } from "../lms/_learnerConfig";

// Audience / area enums used by the asset arrays.
export const SERVICE_AUDIENCES = ["ESHS", "SSHS"];
export const SERVICE_AREAS = ["ESSEX", "SUFFOLK"];

// Base path for the ESHS LMS admin endpoints. The org context is derived
// server-side from the ecauthtoken/ecsecuretoken headers (set by axiosUtility),
// so these calls mirror the rest of the admin API surface.
const LMS_ADMIN = `${GLOBAL_API}/admin/lms/v1`;

// Some authoring features (thumbnail upload, module duplicate, quiz randomize)
// are wired to endpoints the backend may not implement yet.
// Use this to show a friendly "coming soon" message instead of a hard error.
export const isEndpointMissing = (error) => {
  const code = error?.response?.status || error?.response?.data?.status;
  return code === 404 || code === 501;
};

/* ───────────── Org LMS Settings (Approved Email Domains) ─────────────
 * ⚠️ ENDPOINT ASSUMED — the LMS API doc + Postman collection do NOT document an
 * admin route for org-level LMS settings / approved email domains (the field
 * `lmsAutoApproveDomains` lives on the Organization, mutated in the docs only via
 * the super-admin edit-organization route). The user confirmed an admin endpoint
 * exists; adjust the path + request/response shape HERE ONLY once the exact
 * contract is known. Assumed shape:
 *   GET  /admin/lms/v1/settings   -> data.settings.lmsAutoApproveDomains: string[]
 *   POST /admin/lms/v1/settings   body { lmsAutoApproveDomains: string[] }  (204 = soft error)
 */
export const lmsGetSettingsApi = async () => {
  return lmsGet(`${LMS_ADMIN}/settings`);
};

export const lmsUpdateSettingsApi = async (payload) => {
  return lmsPost(`${LMS_ADMIN}/settings`, payload);
};

/* ───────────── Config: Regions & Districts ─────────────
 * Documented + Postman-confirmed. Regions feed the district create form
 * (regionId is required on create); districts feed learner registration. */

export const lmsListRegionsApi = async (includeDisabled = false) => {
  return lmsGet(
    `${LMS_ADMIN}/config/regions${includeDisabled ? "?includeDisabled=true" : ""}`
  );
};

export const lmsSaveRegionApi = async (payload) => {
  return lmsPost(`${LMS_ADMIN}/config/regions`, payload);
};

export const lmsDeleteRegionApi = async (regionId) => {
  return lmsDelete(`${LMS_ADMIN}/config/regions/${regionId}`);
};

export const lmsListDistrictsApi = async ({ regionId, includeDisabled } = {}) => {
  const q = new URLSearchParams();
  if (regionId) q.set("regionId", regionId);
  if (includeDisabled) q.set("includeDisabled", "true");
  const qs = q.toString();
  return lmsGet(`${LMS_ADMIN}/config/districts${qs ? `?${qs}` : ""}`);
};

// Create (omit districtId) or update a district.
export const lmsSaveDistrictApi = async (payload) => {
  return lmsPost(`${LMS_ADMIN}/config/districts`, payload);
};

export const lmsDeleteDistrictApi = async (districtId) => {
  return lmsDelete(`${LMS_ADMIN}/config/districts/${districtId}`);
};

/* ───────────── Dashboard & Reports ───────────── */

export const lmsDashboardApi = async ({ days } = {}) => {
  const q = new URLSearchParams();
  if (days) q.set("days", days);
  const qs = q.toString();
  return lmsGet(`${LMS_ADMIN}/dashboard${qs ? `?${qs}` : ""}`);
};

export const lmsCourseReportApi = async (courseId, query = "") => {
  return lmsGet(`${LMS_ADMIN}/reports/courses/${courseId}${query}`);
};

export const lmsExamResultsApi = async (query = "") => {
  return lmsGet(`${LMS_ADMIN}/reports/exam-results${query}`);
};

export const lmsCertificatesApi = async (query = "") => {
  return lmsGet(`${LMS_ADMIN}/certificates${query}`);
};

export const lmsQuizItemAnalysisApi = async (quizId) => {
  return lmsGet(`${LMS_ADMIN}/reports/quizzes/${quizId}/item-analysis`);
};

/* ───────────── Courses ───────────── */

export const lmsListCoursesApi = async (query = "") => {
  return lmsGet(`${LMS_ADMIN}/courses${query}`);
};

export const lmsGetCourseApi = async (courseIdentifier) => {
  return lmsGet(`${LMS_ADMIN}/courses/${courseIdentifier}`);
};

// Learner-preview endpoints — all read-only (no enrolment/progress/attempt/
// certificate/email/counter is ever created server-side; grade returns
// persisted:false). The course preview is persona-filtered server-side and
// returns signed URLs in each asset's `url`.
export const lmsCoursePreviewApi = async (courseId, { serviceAudience, serviceArea } = {}) => {
  const q = new URLSearchParams();
  if (serviceAudience) q.set("serviceAudience", serviceAudience);
  if (serviceArea) q.set("serviceArea", serviceArea);
  const qs = q.toString();
  return lmsGet(`${LMS_ADMIN}/courses/${courseId}/preview${qs ? `?${qs}` : ""}`);
};

// Learner quiz UI for a saved quiz — questions only, isCorrect/hints stripped.
export const lmsQuizPreviewApi = async (quizId) => {
  return lmsGet(`${LMS_ADMIN}/quizzes/${quizId}/preview`);
};

// Grade a preview attempt server-side (it holds the answers). Body:
// { answers: [{ questionId, selectedOptionIds: [] }] }. No QuizAttempt created.
export const lmsGradeQuizPreviewApi = async (quizId, answers) => {
  return lmsPost(`${LMS_ADMIN}/quizzes/${quizId}/preview/grade`, { answers });
};

// Pre-publish readiness checklist.
export const lmsCourseReadinessApi = async (courseIdentifier) => {
  return lmsGet(`${LMS_ADMIN}/courses/${courseIdentifier}/readiness`);
};

// Create when obj.courseId is empty, update otherwise. Body may include
// introAssets [{serviceAudience, asset}], hasQuizPerSection and
// completionCriteria {...}. Assessments are section-only.
//
// The course `thumbnail` field is an **S3 key from /assets/upload** (NOT a raw
// multipart file — the backend ignores that). So when a File is supplied we
// first upload it, then send its returned key as `thumbnail`. Read endpoints
// sign it back as `thumbnailUrl`.
export const lmsSaveCourseApi = async (obj) => {
  const body = { ...obj };
  const file = body.thumbnailFile;
  delete body.thumbnailFile;

  const uploadThumbnail = async (courseId) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", `${body.title || file.name.replace(/\.[^.]+$/, "")} Thumbnail`);
    fd.append("assetRole", "course-thumbnail");
    fd.append("courseId", courseId);
    if (body.slug) fd.append("courseSlug", body.slug);
    if (body.title) fd.append("courseTitle", body.title);
    return lmsUploadAssetApi(fd);
  };

  if (file instanceof File && !body.courseId) {
    const createRes = await lmsPost(`${LMS_ADMIN}/courses`, body);
    if (createRes?.data?.status !== 200) return createRes;

    const course = createRes?.data?.data?.course || {};
    const courseId = course?._id || course?.courseId;
    if (!courseId) return createRes;

    const up = await uploadThumbnail(courseId);
    // Surface an upload failure to the caller instead of silently saving
    // the course without its thumbnail.
    if (up?.data?.status !== 200) return up;
    const key = up?.data?.data?.asset?.url || up?.data?.data?.asset?.key;
    if (!key) return createRes;

    return lmsPost(`${LMS_ADMIN}/courses`, {
      ...body,
      courseId,
      thumbnail: key,
    });
  }

  if (file instanceof File) {
    const up = await uploadThumbnail(body.courseId);
    if (up?.data?.status !== 200) return up;
    const key = up?.data?.data?.asset?.url || up?.data?.data?.asset?.key;
    if (key) body.thumbnail = key;
  }

  return lmsPost(`${LMS_ADMIN}/courses`, body);
};

/* ───────────── Assets (two-step: upload → attach) ───────────── */

// Step 1: upload one file → returns { data: { asset, previewUrl } }.
// formData fields: file*, title, description, durationSeconds, hasVoiceOver.
export const lmsUploadAssetApi = async (formData, config = {}) => {
  return lmsPost(`${LMS_ADMIN}/assets/upload`, formData, config);
};

/* Course intro assets — one per serviceAudience (ESHS / SSHS). */
export const lmsAddCourseIntroAssetApi = async (courseId, body) => {
  // body: { serviceAudience, asset }
  return lmsPost(`${LMS_ADMIN}/courses/${courseId}/intro-assets`, body);
};
export const lmsUpdateCourseIntroAssetApi = async (courseId, assetId, body) => {
  return lmsPost(
    `${LMS_ADMIN}/courses/${courseId}/intro-assets/${assetId}`,
    body
  );
};
export const lmsDeleteCourseIntroAssetApi = async (courseId, assetId) => {
  return lmsDelete(`${LMS_ADMIN}/courses/${courseId}/intro-assets/${assetId}`);
};

export const lmsGetCourseStatusImpactApi = async (courseId) => {
  return lmsGet(`${LMS_ADMIN}/courses/${courseId}/status-impact`);
};

export const lmsUpdateCourseStatusApi = async (courseId, status, options = {}) => {
  return lmsPost(`${LMS_ADMIN}/courses/${courseId}/status`, {
    status,
    ...options,
  });
};

export const lmsCopyCourseApi = async (courseId) => {
  return lmsPost(`${LMS_ADMIN}/courses/${courseId}/copy`, {});
};

export const lmsSchedulePublishApi = async (courseId, publishAt) => {
  return lmsPost(`${LMS_ADMIN}/courses/${courseId}/status`, {
    status: "scheduled",
    publishAt,
  });
};

export const lmsDeleteCourseApi = async (courseId) => {
  return lmsDelete(`${LMS_ADMIN}/courses/${courseId}`);
};

/* ───────────── Modules ───────────── */

export const lmsListModulesApi = async (courseId) => {
  return lmsGet(`${LMS_ADMIN}/modules/course/${courseId}`);
};

// Whole-module create/update. Body: moduleId(optional), courseId*, title*,
// description, order, completionCriteria{minVideoWatchPercent,requireQuizPass},
// isPublished. Edit asset arrays (introductionAssets / sections /
// completionAssets / supportServicesAssets) via the granular routes below —
// resending `sections` here regenerates section ids and orphans their quizzes.
export const lmsSaveModuleApi = async (formData) => {
  return lmsPost(`${LMS_ADMIN}/modules`, formData);
};

// API expects { courseId, orders: [{ moduleId, order }] }. `orderedIds` is the
// module id list in the desired order; order numbers are 1-based by position.
export const lmsReorderModulesApi = async (courseId, orderedIds) => {
  const orders = orderedIds.map((moduleId, i) => ({ moduleId, order: i + 1 }));
  return lmsPost(`${LMS_ADMIN}/modules/reorder`, { courseId, orders });
};

// Duplicate a module. Assumed endpoint.
export const lmsDuplicateModuleApi = async (moduleId) => {
  return lmsPost(`${LMS_ADMIN}/modules/${moduleId}/duplicate`, {});
};

export const lmsToggleModuleApi = async (moduleId) => {
  return lmsPost(`${LMS_ADMIN}/modules/${moduleId}/toggle-publish`, {});
};

export const lmsDeleteModuleApi = async (moduleId) => {
  return lmsDelete(`${LMS_ADMIN}/modules/${moduleId}`);
};

/* ── Module sections (granular, id-stable) ── */
export const lmsAddSectionApi = async (moduleId, body) => {
  // body: { title*, description, order?, assets?: [asset] }
  return lmsPost(`${LMS_ADMIN}/modules/${moduleId}/sections`, body);
};
export const lmsUpdateSectionApi = async (moduleId, sectionId, body) => {
  return lmsPost(
    `${LMS_ADMIN}/modules/${moduleId}/sections/${sectionId}`,
    body
  );
};
export const lmsDeleteSectionApi = async (moduleId, sectionId) => {
  return lmsDelete(`${LMS_ADMIN}/modules/${moduleId}/sections/${sectionId}`);
};
export const lmsReorderSectionsApi = async (moduleId, sectionIds) => {
  return lmsPost(`${LMS_ADMIN}/modules/${moduleId}/sections/reorder`, {
    orders: sectionIds.map((sectionId, i) => ({ sectionId, order: i + 1 })),
  });
};

/* ── Section assets ── */
export const lmsAddSectionAssetApi = async (moduleId, sectionId, asset) => {
  return lmsPost(
    `${LMS_ADMIN}/modules/${moduleId}/sections/${sectionId}/assets`,
    { asset }
  );
};
export const lmsUpdateSectionAssetApi = async (
  moduleId,
  sectionId,
  assetId,
  asset
) => {
  return lmsPost(
    `${LMS_ADMIN}/modules/${moduleId}/sections/${sectionId}/assets/${assetId}`,
    { asset }
  );
};
export const lmsDeleteSectionAssetApi = async (moduleId, sectionId, assetId) => {
  return lmsDelete(
    `${LMS_ADMIN}/modules/${moduleId}/sections/${sectionId}/assets/${assetId}`
  );
};

/* ── Module intro assets ── */
export const lmsAddModuleIntroAssetApi = async (moduleId, asset) => {
  return lmsPost(`${LMS_ADMIN}/modules/${moduleId}/intro-assets`, { asset });
};
export const lmsUpdateModuleIntroAssetApi = async (moduleId, assetId, asset) => {
  return lmsPost(
    `${LMS_ADMIN}/modules/${moduleId}/intro-assets/${assetId}`,
    { asset }
  );
};
export const lmsDeleteModuleIntroAssetApi = async (moduleId, assetId) => {
  return lmsDelete(`${LMS_ADMIN}/modules/${moduleId}/intro-assets/${assetId}`);
};

/* ── Module completion assets ── */
export const lmsAddCompletionAssetApi = async (moduleId, asset) => {
  return lmsPost(`${LMS_ADMIN}/modules/${moduleId}/completion-assets`, {
    asset,
  });
};
export const lmsUpdateCompletionAssetApi = async (moduleId, assetId, asset) => {
  return lmsPost(
    `${LMS_ADMIN}/modules/${moduleId}/completion-assets/${assetId}`,
    { asset }
  );
};
export const lmsDeleteCompletionAssetApi = async (moduleId, assetId) => {
  return lmsDelete(
    `${LMS_ADMIN}/modules/${moduleId}/completion-assets/${assetId}`
  );
};

/* ── Module support-service assets — one per serviceArea (ESSEX / SUFFOLK) ── */
export const lmsAddSupportAssetApi = async (moduleId, body) => {
  // body: { serviceArea, asset }
  return lmsPost(`${LMS_ADMIN}/modules/${moduleId}/support-assets`, body);
};
export const lmsUpdateSupportAssetApi = async (moduleId, assetId, body) => {
  return lmsPost(
    `${LMS_ADMIN}/modules/${moduleId}/support-assets/${assetId}`,
    body
  );
};
export const lmsDeleteSupportAssetApi = async (moduleId, assetId) => {
  return lmsDelete(
    `${LMS_ADMIN}/modules/${moduleId}/support-assets/${assetId}`
  );
};

/* ───────────── Quizzes ───────────── */

// Create/update. Body shape:
//   { quizId?, scope: "section"|"module"|"course",
//     refId: <module id for section/module, course id for course>,
//     sectionId: <required when scope==="section">,
//     passMark, maxAttempts, timeLimitMinutes, randomizeQuestions?,
//     questions: [{ order, questionText, marks, explanation,
//                   options: [{ text, isCorrect, hint }] }],
//     publish: bool }
// Published quiz changes are versioned by the backend and released with the course.
export const lmsSaveQuizApi = async (obj) => {
  return lmsPost(`${LMS_ADMIN}/quizzes`, obj);
};

export const lmsGetQuizApi = async (quizId) => {
  return lmsGet(`${LMS_ADMIN}/quizzes/${quizId}`);
};

export const lmsUpdateQuizStatusApi = async (quizId, status) => {
  return lmsPost(`${LMS_ADMIN}/quizzes/${quizId}/status`, { status });
};

export const lmsDeleteQuizApi = async (quizId) => {
  return lmsDelete(`${LMS_ADMIN}/quizzes/${quizId}`);
};

/* ───────────── Registration Requests ───────────── */

export const lmsListRegistrationsApi = async (query = "") => {
  return lmsGet(`${LMS_ADMIN}/registrations${query}`);
};

// action: "approve" | "reject"; reason optional for reject
export const lmsReviewRegistrationApi = async (requestId, obj) => {
  return lmsPost(`${LMS_ADMIN}/registrations/${requestId}/review`, {
    ...obj,
    ...(obj?.action === "approve" && !obj?.setPasswordBaseUrl
      ? { setPasswordBaseUrl: setPasswordBaseUrl() }
      : {}),
  });
};

export const lmsResendRegistrationInviteApi = async (requestId) => {
  return lmsPost(`${LMS_ADMIN}/registrations/${requestId}/resend-invite`, {
    setPasswordBaseUrl: setPasswordBaseUrl(),
  });
};

/* ───────────── Newsletter subscribers ───────────── */
// Per 15-lms-api.md / postman: admin-managed list; learners who opt in at
// registration (newsletterOptIn) are upserted with source:"registration".
// GET list query: search, subscribed (true|false), page, limit → { list,
// pagination{page,limit,total,pages}, counts{subscribed} }. Create upserts by
// email (source:"admin"). Update is POST /newsletter/:id (NOT PUT/PATCH);
// isSubscribed:false unsubscribes. DELETE soft-deletes.
export const lmsListNewsletterApi = async (query = "") => {
  return lmsGet(`${LMS_ADMIN}/newsletter${query}`);
};

export const lmsCreateNewsletterApi = async (obj) => {
  // body: { email*, firstName?, lastName? }
  return lmsPost(`${LMS_ADMIN}/newsletter`, obj);
};

export const lmsUpdateNewsletterApi = async (id, obj) => {
  // body: { email?, firstName?, lastName?, isSubscribed? }
  return lmsPost(`${LMS_ADMIN}/newsletter/${id}`, obj);
};

export const lmsDeleteNewsletterApi = async (id) => {
  return lmsDelete(`${LMS_ADMIN}/newsletter/${id}`);
};

/* ───────────── Learners ───────────── */

export const lmsListLearnerAccountsApi = async (query = "") => {
  return lmsGet(`${LMS_ADMIN}/learner-accounts${query}`);
};

export const lmsListLearnersApi = async (query = "") => {
  return lmsGet(`${LMS_ADMIN}/learners${query}`);
};

export const lmsGetLearnerApi = async (learnerId) => {
  return lmsGet(`${LMS_ADMIN}/learners/${learnerId}`);
};

export const lmsCreateLearnerApi = async (obj) => {
  return lmsPost(`${LMS_ADMIN}/learners`, {
    ...obj,
    setPasswordBaseUrl: obj?.setPasswordBaseUrl || setPasswordBaseUrl(),
  });
};

export const lmsImportLearnersApi = async (formData) => {
  return lmsPost(`${LMS_ADMIN}/learners/import`, formData);
};

export const lmsUpdateLearnerStatusApi = async (learnerId, status) => {
  return lmsPost(`${LMS_ADMIN}/learners/${learnerId}/status`, { status });
};

export const lmsUpdateLearnerProfileApi = async (learnerId, obj) => {
  return lmsPost(`${LMS_ADMIN}/learners/${learnerId}`, obj);
};

export const lmsSendLearnerPasswordResetApi = async (learnerId) => {
  return lmsPost(`${LMS_ADMIN}/learners/${learnerId}/send-password-reset`, {
    setPasswordBaseUrl: setPasswordBaseUrl(),
  });
};

export const lmsResendInviteApi = async (learnerId) => {
  return lmsPost(`${LMS_ADMIN}/learners/${learnerId}/resend-invite`, {
    setPasswordBaseUrl: setPasswordBaseUrl(),
  });
};

export const lmsDeleteLearnerApi = async (learnerId) => {
  return lmsDelete(`${LMS_ADMIN}/learners/${learnerId}`);
};
